import React from 'react';
import { Download, FileHeart } from 'lucide-react';
import type { TestimonialPostData } from '../../types';
import { MentionText } from '@/components/mentions/MentionText';
import { Button } from '@/components/ui/button';
import parseFileName from './FileNameParser';

interface TestimonialPostContentProps {
  post: TestimonialPostData;
}

const getDownloadUrl = (url: string) => {
  if (!url) return '';
  if (url.includes('res.cloudinary.com') && !url.includes('fl_attachment')) {
    return url.replace('/upload/', '/upload/fl_attachment/');
  }
  return url;
};

export function TestimonialPostContent({ post }: TestimonialPostContentProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 mb-0">
          <FileHeart className="w-5 h-5 text-yellow-500" />
          <span className="font-bold text-foreground">Testimonial</span>
        </div>
        <h3 className="font-semibold text-lg m-0">{post.testimonialTitle}</h3>
        {post.testimonialRecipientFirstName && (
          <p className="text-sm text-muted-foreground m-0">
            For:{' '}
            <span className="text-lg text-muted-foreground font-bold">
              {post.testimonialRecipientFirstName || post.testimonialRecipientLastName
                ? `${post.testimonialRecipientFirstName ?? ''} ${post.testimonialRecipientLastName ?? ''}`.trim()
                : null}
            </span>
          </p>
        )}
      </div>
      <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 p-4 rounded-xl border border-yellow-500/20">
        <p className="whitespace-pre-wrap text-sm text-foreground/90 italic">
          "<MentionText text={post.testimonialContent || ''} />"
        </p>
      </div>
      {post.testimonialAttachments && post.testimonialAttachments.length > 0 && (
        <>
          <h4 className="text-sm font-semibold text-foreground/80 mb-2 border-b border-border/50 pb-2">
            Attachments
          </h4>
          <div className="flex flex-col gap-3">
            {post.testimonialAttachments.map((attachment, index) => (
              <a
                key={index}
                href={getDownloadUrl(attachment)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between w-full p-3 rounded-md border border-border/50 bg-muted/30 hover:bg-muted/50 transition-colors group"
                download
              >
                <div className="flex items-center gap-2 flex-1 min-w-0 mr-4">
                  <span className="text-sm font-medium text-foreground/90 truncate">
                    {parseFileName(attachment)}
                  </span>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 px-2.5 sm:px-3 shadow-sm"
                  asChild
                >
                  <div className="cursor-pointer pointer-events-none flex items-center justify-center">
                    <Download className="w-4 h-4 sm:w-3.5 sm:h-3.5 sm:mr-2" />
                    <span className="hidden sm:inline text-xs">Download</span>
                  </div>
                </Button>
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
